import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import PublicPersonas from "./components/PublicPersonas";
import HeroMockup from "./components/landing/HeroMockup";
import EngineShowcase from "./components/landing/EngineShowcase";
import ComparisonTable from "./components/landing/ComparisonTable";
import ComingSoonRibbon from "./components/landing/ComingSoonRibbon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage({ onLogin }) {
  // ── Waitlist email capture ───────────────────────────────────────────────
  const [email, setEmail] = useState("");
  const [wlStatus, setWlStatus] = useState("idle"); // idle | loading | success | already | error
  const [wlMsg, setWlMsg] = useState("");
  const [wlPosition, setWlPosition] = useState(null);
  const [waitlistCount, setWaitlistCount] = useState(0);

  // ── Admin login modal ────────────────────────────────────────────────────
  const [showAdmin, setShowAdmin] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/waitlist/count`).then(r => setWaitlistCount(r.data?.total || 0)).catch(() => {});
  }, []);

  const validEmail = useMemo(() =>
    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email.trim())
  , [email]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!validEmail) { setWlStatus("error"); setWlMsg("Please enter a valid email address."); return; }
    setWlStatus("loading"); setWlMsg("");
    try {
      const r = await axios.post(`${API}/waitlist/subscribe`, { email: email.trim() });
      setWlPosition(r.data?.position || null);
      setWaitlistCount(r.data?.total || waitlistCount + 1);
      setWlStatus(r.data?.alreadySubscribed ? "already" : "success");
      setWlMsg(r.data?.alreadySubscribed
        ? `You're already on the list — position #${r.data.position}.`
        : `You're in. Position #${r.data.position}.`);
    } catch (err) {
      setWlStatus("error");
      setWlMsg(err?.response?.data?.detail || "Could not subscribe. Try again.");
    }
  };

  // ── Admin password strength meter (visual only, non-blocking) ────────────
  const strength = useMemo(() => {
    let s = 0;
    if (password.length >= 6) s++;
    if (password.length >= 10) s++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) s++;
    if (/\d/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return Math.min(4, s);
  }, [password]);
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["#374151", "#f87171", "#fbbf24", "#60a5fa", "#4ade80"][strength];

  const [resetting, setResetting] = useState(false);

  // ── Forgot-password / magic-link flow (iter 124) ──
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMode, setForgotMode] = useState("magic"); // "magic" | "reset"
  const [forgotStatus, setForgotStatus] = useState("idle"); // idle | loading | sent | error
  const [forgotMsg, setForgotMsg] = useState("");
  const [devMagicLink, setDevMagicLink] = useState(""); // surfaced when Resend offline
  // ── New-password capture (after magic-link click in reset mode) ──
  const [resetTicket, setResetTicket] = useState(""); // populated from /auth/magic/{token} in reset mode
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordSubmitting, setNewPasswordSubmitting] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setError(""); setLoading(true);
    try { await onLogin(username.trim(), password); }
    catch (err) { setError(err?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  const handleResetLockout = async () => {
    setResetting(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-lockout`, { method: "POST" });
      if (r.ok) { setError("Lockout cleared. Try again."); }
      else { setError("Could not reset. Wait 15 minutes or contact support."); }
    } catch { setError("Could not reset. Wait 15 minutes or contact support."); }
    finally { setResetting(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotStatus("loading"); setForgotMsg(""); setDevMagicLink("");
    try {
      const r = await axios.post(`${API}/auth/forgot`, { email: forgotEmail.trim().toLowerCase(), mode: forgotMode });
      setForgotStatus("sent");
      setForgotMsg(
        forgotMode === "magic"
          ? "If that email exists in our system, a sign-in link is on its way."
          : "If that email exists in our system, a password-reset link is on its way."
      );
      if (r.data?.devLink) setDevMagicLink(r.data.devLink);
    } catch (err) {
      setForgotStatus("error");
      setForgotMsg(err?.response?.data?.detail || "Could not send link");
    }
  };

  // ── Detect ?magic=…&mode=… on landing and consume it ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("magic"); const m = params.get("mode") || "magic";
    if (!t) return;
    // Strip the magic from the URL so refresh doesn't re-trigger.
    const cleanUrl = `${window.location.origin}${window.location.pathname}`;
    window.history.replaceState({}, "", cleanUrl);
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/magic/${encodeURIComponent(t)}`);
        if (r.data?.mode === "reset" && r.data?.resetToken) {
          setResetTicket(r.data.resetToken);
          setShowAdmin(true);
        } else if (r.data?.mode === "magic" && r.data?.user_id) {
          // The server already set the session cookie on this response —
          // just reload so AuthProvider's checkAuth() picks it up.
          window.location.reload();
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Magic link is invalid or expired");
        setShowAdmin(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setNewPasswordSubmitting(true); setNewPasswordError("");
    try {
      const r = await axios.post(`${API}/auth/reset`, { resetToken: resetTicket, password: newPassword });
      if (r.data?.ok && r.data?.user_id) {
        // Session cookie already set by the server — reload to pick it up.
        window.location.reload();
      }
    } catch (err) {
      setNewPasswordError(err?.response?.data?.detail || "Could not set new password");
    } finally { setNewPasswordSubmitting(false); }
  };

  return (
    <div style={{
      background: "radial-gradient(1200px 600px at 50% -10%, rgba(29,78,216,0.25), transparent), radial-gradient(800px 400px at 20% 40%, rgba(16,185,129,0.08), transparent), #060a12",
      color: "#e6edf3", minHeight: "100vh", fontFamily: "'Outfit', -apple-system, sans-serif",
      display: "flex", flexDirection: "column",
    }}>
      {/* Animated particle-ish BG + shimmer skeleton keyframe */}
      <style>{`
        @keyframes pulse{0%,100%{opacity:.7}50%{opacity:1}}
        @keyframes floatDots{0%{transform:translateY(0) scale(1)}50%{transform:translateY(-6px) scale(1.04)}100%{transform:translateY(0) scale(1)}}
        @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
      `}</style>

      {/* Nav */}
      <nav style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "calc(20px + env(safe-area-inset-top)) 28px 20px", maxWidth: 1200, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, #1d4ed8, #0ea5e9)", display: "grid", placeItems: "center", fontSize: 14, fontWeight: 700, color: "#fff" }}>ZI</div>
          <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.5 }}>Omega ZI AI</span>
        </div>
      </nav>

      {/* Hero — Coming Soon · 2-column at ≥980px */}
      <section style={{
        padding: "40px 24px 30px", maxWidth: 1200, margin: "0 auto", width: "100%", boxSizing: "border-box", flex: 1,
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 36,
        alignItems: "center",
      }}>
        <style>{`
          @media (min-width: 980px) {
            .hero-grid { grid-template-columns: 1.05fr 0.95fr !important; gap: 56px !important; padding-top: 60px !important; }
            .hero-copy { text-align: left !important; }
            .hero-copy h1 { font-size: 64px !important; }
            .hero-copy form { justify-content: flex-start !important; }
            .hero-copy .avatars-row { justify-content: flex-start !important; }
            .hero-copy .coming-pill { margin-left: 0 !important; }
          }
          @keyframes floatY { 0%,100%{transform:translateY(0) perspective(1200px) rotateX(2deg) rotateY(-1.5deg)} 50%{transform:translateY(-8px) perspective(1200px) rotateX(2deg) rotateY(-1.5deg)} }
        `}</style>

        <div className="hero-grid" style={{
          display: "grid", gridTemplateColumns: "1fr", gap: 36,
          width: "100%", alignItems: "center",
        }}>
          {/* Copy column */}
          <div className="hero-copy" style={{ textAlign: "center" }}>
            <div className="coming-pill" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 999, background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.3)", marginBottom: 20, marginLeft: "auto" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa", animation: "pulse 1.6s infinite" }} />
              <span style={{ fontSize: 10, fontWeight: 800, color: "#a78bfa", letterSpacing: 2.5 }}>COMING Q4 · 2027</span>
            </div>

            <h1 style={{ fontSize: "clamp(40px, 6vw, 64px)", fontWeight: 800, lineHeight: 1.04, marginBottom: 18, letterSpacing: -1.2 }}>
              Trade like a quant.<br />
              <span style={{ background: "linear-gradient(120deg, #a78bfa, #60a5fa, #4ade80)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Without being one.
              </span>
            </h1>
            <p style={{ fontSize: 16, color: "#8b949e", maxWidth: 520, margin: "0 auto 28px", lineHeight: 1.6 }}>
              Omega ZI AI 6.0 is the first options co-pilot that runs <strong style={{ color: "#e6edf3" }}>5 institutional engines</strong> in parallel — Monte Carlo, Claude reasoning, Guardian watch, Auto-Trim, and a Sunday replay digest. Your phone, your edge.
            </p>

            {/* Email capture */}
            <form onSubmit={handleSubscribe} style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 520, marginBottom: 12 }}>
              <input
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={e => { setEmail(e.target.value); if (wlStatus !== "idle") setWlStatus("idle"); }}
                data-testid="waitlist-email-input"
                disabled={wlStatus === "loading" || wlStatus === "success"}
                style={{
                  flex: "1 1 240px", minWidth: 0, padding: "15px 18px", borderRadius: 12,
                  border: `1px solid ${wlStatus === "error" ? "rgba(248,113,113,0.5)" : "rgba(255,255,255,0.12)"}`,
                  background: "rgba(255,255,255,0.04)", color: "#e6edf3",
                  fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box",
                }}
              />
              <button
                type="submit"
                data-testid="waitlist-submit"
                disabled={wlStatus === "loading" || wlStatus === "success"}
                style={{
                  flex: "0 0 auto", padding: "15px 26px", borderRadius: 12, border: "none",
                  background: wlStatus === "success" ? "#166534" : "linear-gradient(135deg, #a78bfa, #3b82f6)",
                  color: "#fff", cursor: wlStatus === "loading" ? "not-allowed" : "pointer",
                  fontFamily: "inherit", fontSize: 14, fontWeight: 700, letterSpacing: 0.3,
                  boxShadow: "0 12px 32px rgba(167,139,250,0.32)",
                  opacity: wlStatus === "loading" ? 0.7 : 1,
                  transition: "transform 120ms ease, box-shadow 120ms ease",
                }}
                onMouseDown={e => { e.currentTarget.style.transform = "scale(0.97)"; }}
                onMouseUp={e => { e.currentTarget.style.transform = ""; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; }}
                onTouchStart={e => { e.currentTarget.style.transform = "scale(0.97)"; }}
                onTouchEnd={e => { e.currentTarget.style.transform = ""; }}
                onTouchCancel={e => { e.currentTarget.style.transform = ""; }}
              >
                {wlStatus === "loading" ? "Joining…" :
                 wlStatus === "success" ? "✓ You're in" :
                 wlStatus === "already" ? "Already on list" :
                 "Claim early access"}
              </button>
            </form>

            <div data-testid="waitlist-msg-slot" style={{
              minHeight: 24, marginBottom: 10, fontSize: 13,
              color: wlStatus === "success" || wlStatus === "already" ? "#4ade80" : "#f87171",
              transition: "opacity 200ms ease",
              opacity: wlMsg ? 1 : 0,
            }}>
              {wlMsg || "\u00A0"}
            </div>

            <div className="avatars-row" style={{ fontSize: 13, color: "#636e7b", display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
              <span style={{ display: "inline-flex" }}>
                {[0,1,2,3,4].map(i => (
                  <span key={i} style={{
                    width: 22, height: 22, borderRadius: "50%", marginLeft: i > 0 ? -7 : 0,
                    background: `linear-gradient(135deg, ${["#3b82f6","#0ea5e9","#10b981","#f59e0b","#a78bfa"][i]}, ${["#1d4ed8","#0284c7","#047857","#b45309","#7c3aed"][i]})`,
                    border: "2px solid #060a12",
                    flex: "0 0 auto",
                  }} />
                ))}
              </span>
              <span data-testid="waitlist-count" style={{ minWidth: 40 }}>
                {waitlistCount > 0
                  ? <><strong style={{ color: "#e6edf3" }}>{waitlistCount.toLocaleString()}</strong> traders on the waitlist</>
                  : <span style={{
                      display: "inline-block", width: 110, height: 12, borderRadius: 6,
                      background: "linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.12), rgba(255,255,255,0.05))",
                      backgroundSize: "200% 100%", animation: "shimmer 1.4s linear infinite",
                      verticalAlign: "middle",
                    }} />
                }
              </span>
            </div>

            {wlPosition != null && (wlStatus === "success" || wlStatus === "already") && (
              <div style={{ marginTop: 22, padding: 14, borderRadius: 12, background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.22)", maxWidth: 360 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#4ade80", letterSpacing: 1.5 }}>YOUR POSITION</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#e6edf3", marginTop: 4 }}>#{wlPosition}</div>
                <div style={{ fontSize: 11, color: "#8b949e", marginTop: 2 }}>Beta drops Q2 2027 — full launch Q4 2027.</div>
              </div>
            )}
          </div>

          {/* Mockup column — animated dashboard preview */}
          <div style={{ animation: "floatY 6s ease-in-out infinite", padding: "0 8px" }}>
            <HeroMockup />
          </div>
        </div>
      </section>

      {/* Coming-Soon countdown ribbon */}
      <ComingSoonRibbon />

      {/* 5-engine interactive showcase */}
      <EngineShowcase />

      {/* Comparison vs other apps */}
      <ComparisonTable />

      {/* What's coming */}
      <section id="whats-coming" style={{ padding: "60px 24px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        <div style={{ fontSize: 11, color: "#60a5fa", letterSpacing: 2, textAlign: "center", fontWeight: 700, marginBottom: 10 }}>WHAT'S COMING</div>
        <h2 style={{ fontSize: 32, fontWeight: 800, textAlign: "center", marginBottom: 40, letterSpacing: -0.6 }}>Elite-grade options intelligence</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          {[
            { icon: "★", color: "#fbbf24", title: "Elite Contract Scorer", desc: "Black-Scholes PoP + Monte Carlo cross-check + Kelly sizing per contract. Know the exact edge before you pull the trigger." },
            { icon: "⚡", color: "#60a5fa", title: "Full-Auto Omega Pilot", desc: "12-vector Zi score, regime-adaptive weights, drawdown-aware risk gate. Let Omega trade while you sleep." },
            { icon: "🌐", color: "#4ade80", title: "Universe Scanner", desc: "Amcor to Chipotle — tiered picks by price band. Small-cap opportunities and mega-cap setups side-by-side." },
            { icon: "🔔", color: "#a78bfa", title: "Claude-Powered Alerts", desc: "Every notification opens an Omega AI deep-dive with call/put forecast chart, verdict, and one-sentence recommendation." },
            { icon: "📈", color: "#f87171", title: "Dynamic Exit Verdicts", desc: "Holding, scaling out, or exiting — Omega rationalizes every move with theta burn, IV crush risk, and gain context." },
            { icon: "🧠", color: "#22d3ee", title: "Decision Journal", desc: "Replay every AI decision with animated 12-vector breakdown. Learn from your co-pilot's playbook." },
          ].map((f, i) => (
            <div key={i} style={{ padding: 20, borderRadius: 16, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ fontSize: 22, color: f.color, marginBottom: 10 }}>{f.icon}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#e6edf3", marginBottom: 6 }}>{f.title}</div>
              <div style={{ fontSize: 12, color: "#8b949e", lineHeight: 1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Public User Personas — social-proof carousel (iter 115) */}
      <PublicPersonas />

      {/* Stats strip */}
      <section style={{ padding: "40px 24px 60px", maxWidth: 1000, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 20, padding: 28, borderRadius: 20, background: "linear-gradient(135deg, rgba(29,78,216,0.1), rgba(16,185,129,0.05))", border: "1px solid rgba(255,255,255,0.06)" }}>
          {[
            { val: "10K+", label: "Monte Carlo paths / trade" },
            { val: "12", label: "Decision vectors" },
            { val: "4", label: "Price tiers scanned" },
            { val: "<500ms", label: "Alert → Phone" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: "#e6edf3", letterSpacing: -0.5 }}>{s.val}</div>
              <div style={{ fontSize: 11, color: "#8b949e", marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "28px 28px calc(24px + env(safe-area-inset-bottom))", textAlign: "center" }}>
        <button
          onClick={() => setShowAdmin(true)}
          data-testid="admin-signin-link"
          style={{ fontSize: 13, color: "#8b949e", background: "transparent", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "11px 22px", cursor: "pointer", fontFamily: "inherit", marginBottom: 18 }}
        >Administrator Sign-In →</button>
        <p style={{ fontSize: 11, color: "#444c56", margin: 0 }}>Omega ZI AI 6.0 · Zi Probability Engine · Public.com-powered · Beta Q2 2027 · Full launch Q4 2027</p>
      </footer>

      {/* Admin login modal */}
      {showAdmin && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={e => e.target === e.currentTarget && setShowAdmin(false)}
        >
          <div style={{ background: "#0d1117", border: "1px solid rgba(167,139,250,0.22)", borderRadius: 18, padding: "34px 28px", maxWidth: 380, width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg, #a78bfa, #60a5fa)", display: "grid", placeItems: "center", fontSize: 14, fontWeight: 800, color: "#fff" }}>ZI</div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", letterSpacing: 2 }}>RESTRICTED</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Administrator Sign-In</div>
              </div>
              <button onClick={() => setShowAdmin(false)} style={{ marginLeft: "auto", background: "none", border: "none", color: "#8b949e", fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
            </div>
            <form onSubmit={handleLogin}>
              {error && <div style={{ color: "#f87171", fontSize: 12, marginBottom: 12, padding: "8px 10px", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 8 }} data-testid="login-error">
                {error}
                {/Too many|locked/i.test(error) && (
                  <button
                    type="button"
                    data-testid="login-reset-lockout"
                    onClick={handleResetLockout}
                    disabled={resetting}
                    style={{ marginLeft: 8, padding: "3px 9px", borderRadius: 6, border: "1px solid rgba(248,113,113,0.4)", background: "rgba(248,113,113,0.15)", color: "#fca5a5", fontSize: 10, fontWeight: 700, cursor: resetting ? "wait" : "pointer", fontFamily: "inherit" }}
                  >{resetting ? "Resetting…" : "Reset lockout"}</button>
                )}
              </div>}
              <input type="text" placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} autoFocus
                style={{ width: "100%", padding: "13px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#e6edf3", fontSize: 14, marginBottom: 10, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} data-testid="login-username" />
              <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
                style={{ width: "100%", padding: "13px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#e6edf3", fontSize: 14, marginBottom: 8, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} data-testid="login-password" />

              {/* Password strength meter (visual only) */}
              {password && (
                <div data-testid="password-strength" style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", gap: 4, marginBottom: 5 }}>
                    {[0,1,2,3].map(i => (
                      <div key={i} style={{
                        flex: 1, height: 3, borderRadius: 2,
                        background: i < strength ? strengthColor : "rgba(255,255,255,0.08)",
                        transition: "background 160ms",
                      }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: strengthColor, letterSpacing: 1 }}>{strengthLabel}</div>
                </div>
              )}

              <button type="submit" disabled={loading}
                style={{ width: "100%", padding: "13px", borderRadius: 10, border: "1px solid rgba(167,139,250,0.3)", background: "linear-gradient(135deg, rgba(167,139,250,0.2), rgba(96,165,250,0.1))", color: "#e6edf3", cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 700, letterSpacing: 0.3, opacity: loading ? 0.7 : 1 }} data-testid="login-submit">
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>

            {/* New-password capture (after magic-link in reset mode) */}
            {resetTicket && (
              <form onSubmit={submitNewPassword} style={{ marginTop: 18, padding: 14, borderRadius: 10, background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.18)" }} data-testid="reset-password-form">
                <div style={{ fontSize: 10, fontWeight: 700, color: "#4ade80", letterSpacing: 1.5, marginBottom: 8 }}>SET A NEW PASSWORD</div>
                <input
                  type="password"
                  placeholder="New password (min 4 chars)"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required minLength={4}
                  data-testid="reset-password-input"
                  style={{ width: "100%", padding: "11px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "#e6edf3", fontSize: 13, marginBottom: 8, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
                />
                {newPasswordError && <div style={{ color: "#f87171", fontSize: 11, marginBottom: 8 }}>{newPasswordError}</div>}
                <button type="submit" disabled={newPasswordSubmitting || newPassword.length < 4} data-testid="reset-password-submit"
                  style={{ width: "100%", padding: "10px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #4ade80, #047857)", color: "#fff", fontWeight: 700, fontSize: 12, cursor: newPasswordSubmitting ? "wait" : "pointer", fontFamily: "inherit", opacity: newPassword.length < 4 ? 0.6 : 1 }}>
                  {newPasswordSubmitting ? "Saving…" : "Save & Sign In"}
                </button>
              </form>
            )}

            {/* Forgot-password / magic-link entry */}
            {!resetTicket && (
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => { setForgotOpen(true); setForgotEmail(""); setForgotMode("magic"); setForgotStatus("idle"); setForgotMsg(""); setDevMagicLink(""); }}
                  style={{ background: "none", border: "none", color: "#a78bfa", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "6px 8px", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 3 }}
                >Forgot password? Email me a sign-in link</button>
              </div>
            )}

            <div style={{ fontSize: 10, color: "#444c56", textAlign: "center", marginTop: 14, lineHeight: 1.5 }}>
              Administrator access only. Public sign-up is on the waitlist.
            </div>
          </div>
        </div>
      )}

      {/* Forgot-password modal */}
      {forgotOpen && (
        <div onClick={() => setForgotOpen(false)} data-testid="forgot-modal-backdrop"
          style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1100 }}>
          <div onClick={e => e.stopPropagation()} data-testid="forgot-modal"
            style={{ background: "#0d1117", border: "1px solid rgba(167,139,250,0.22)", borderRadius: 16, padding: 28, width: "100%", maxWidth: 420, color: "#e6edf3", boxShadow: "0 30px 80px rgba(2,6,23,0.55)" }}>
            <div style={{ fontSize: 10, color: "#a78bfa", letterSpacing: 2, fontWeight: 800, marginBottom: 6 }}>OMEGA ZI · ACCESS</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, marginTop: 0, marginBottom: 6 }}>Email me a sign-in link</h3>
            <p style={{ fontSize: 12, color: "#8b949e", marginBottom: 14, lineHeight: 1.55 }}>
              We'll send a one-time link that signs you in instantly — no password needed.
              Choose <em>Reset</em> if you'd rather set a new password.
            </p>

            {/* Mode toggle */}
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {[
                { id: "magic", label: "Magic link" },
                { id: "reset", label: "Reset password" },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`forgot-mode-${opt.id}`}
                  onClick={() => setForgotMode(opt.id)}
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: 8,
                    border: `1px solid ${forgotMode === opt.id ? "rgba(167,139,250,0.5)" : "rgba(255,255,255,0.08)"}`,
                    background: forgotMode === opt.id ? "rgba(167,139,250,0.18)" : "rgba(255,255,255,0.03)",
                    color: forgotMode === opt.id ? "#a78bfa" : "#8b949e",
                    fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}
                >{opt.label}</button>
              ))}
            </div>

            <form onSubmit={submitForgot}>
              <input
                type="email"
                placeholder="you@email.com"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                required disabled={forgotStatus === "loading" || forgotStatus === "sent"}
                data-testid="forgot-email-input"
                style={{ width: "100%", padding: "13px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#e6edf3", fontSize: 14, marginBottom: 10, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
              />
              <button
                type="submit"
                disabled={forgotStatus === "loading" || forgotStatus === "sent"}
                data-testid="forgot-submit"
                style={{ width: "100%", padding: "13px", borderRadius: 10, border: "1px solid rgba(167,139,250,0.3)", background: "linear-gradient(135deg, #a78bfa, #3b82f6)", color: "#fff", cursor: forgotStatus === "loading" ? "wait" : "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 700, letterSpacing: 0.3, opacity: forgotStatus === "loading" ? 0.7 : 1 }}>
                {forgotStatus === "loading" ? "Sending…" :
                 forgotStatus === "sent" ? "✓ Sent" :
                 forgotMode === "magic" ? "Email me a sign-in link" : "Email me a reset link"}
              </button>
            </form>

            {forgotMsg && (
              <div data-testid="forgot-msg" style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, fontSize: 12, lineHeight: 1.55, color: forgotStatus === "sent" ? "#4ade80" : "#f87171", background: forgotStatus === "sent" ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)", border: `1px solid ${forgotStatus === "sent" ? "rgba(74,222,128,0.22)" : "rgba(248,113,113,0.22)"}` }}>
                {forgotMsg}
              </div>
            )}
            {devMagicLink && (
              <div data-testid="forgot-dev-link" style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, fontSize: 11, lineHeight: 1.55, color: "#fbbf24", background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)" }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ Email is offline (no Resend key)</div>
                Open the link directly to finish: <br />
                <a href={devMagicLink} style={{ color: "#fbbf24", wordBreak: "break-all", textDecoration: "underline" }}>{devMagicLink}</a>
              </div>
            )}

            <button
              type="button"
              data-testid="forgot-close"
              onClick={() => setForgotOpen(false)}
              style={{ width: "100%", marginTop: 14, padding: "8px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)", color: "#8b949e", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
            >Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
